/**
 * Central registry of per-org feature flags keyed on `orgDomain`.
 * Add new org-scoped behavior here instead of scattering `Set`s across
 * components — session, editor, and voice/publish gating all read from
 * this module.
 */

import type { ProviderId, SessionMode } from "@/lib/powtoon-be/types";

/** Orgs routed through the BYO LiveKit flow instead of Heygen-managed sessions. */
const BYO_LIVEKIT_ORGS: ReadonlySet<string> = new Set(["lockheedmartin", "qa-prod"]);

/**
 * Extra orgs restricted to imported (private) voices only in the editor.
 * BYO LiveKit orgs are always restricted too — see `isImportedVoicesOnlyOrg`.
 */
const IMPORTED_VOICES_ONLY_ORGS: ReadonlySet<string> = new Set(["lockheedmartin", "qa-prod"]);

/** Orgs allowed to select the "Upsell" workspace publish mode. */
const UPSELL_PUBLISH_MODE_ORGS: ReadonlySet<string> = new Set(["powtoon", "visualnative"]);

/** Orgs allowed to use the Tavus provider (in addition to LiveAvatar). */
const TAVUS_PROVIDER_ORGS: ReadonlySet<string> = new Set(["visualnative", "qa-staging", "ourtoon", "qa-prod"]);

/** Orgs restricted to Tavus as their only provider (no LiveAvatar). */
const TAVUS_ONLY_ORGS: ReadonlySet<string> = new Set(["omnitechmedical"]);

/** Orgs allowed to use the D-ID provider (in addition to LiveAvatar). */
const DID_PROVIDER_ORGS: ReadonlySet<string> = new Set(["visualnative", "qa-staging", "qa-prod", "ourtoon"]);

/** Orgs allowed to use the Kaltura provider (in addition to LiveAvatar). */
const KALTURA_PROVIDER_ORGS: ReadonlySet<string> = new Set(["qa-staging", "qa-prod", "visualnative", "ourtoon"]);

/** Orgs allowed to import scenarios from the legacy studio. */
const LEGACY_IMPORT_ORGS: ReadonlySet<string> = new Set(["ourtoon", "visualnative"]);

/**
 * Orgs that also see the LiveAvatar account's own custom ("user") avatars in
 * the avatar picker, on top of the public catalogue. Everyone else keeps the
 * public list only.
 */
const USER_AVATAR_ORGS: ReadonlySet<string> = new Set(["qa-prod"]);

/**
 * Orgs with access to live (real-data) analytics. Everyone else keeps the
 * demo dataset only — the Demo/Live source switch is not rendered for them.
 */
const LIVE_ANALYTICS_ORGS: ReadonlySet<string> = new Set(["ourtoon", "visualnative", "qa-staging"]);

const has = (set: ReadonlySet<string>, d?: string | null) => !!d && set.has(d.toLowerCase());

export const isByoLivekitOrg = (orgDomain?: string | null) => has(BYO_LIVEKIT_ORGS, orgDomain);

/**
 * Known tenants use today's policy. Anonymous public links have no tenant, so
 * they use HeyGen-managed LiveKit regardless of an old "lite" stamp — unless
 * the avatar itself explicitly forces BYO (`force_byo_livekit`), which wins
 * for every viewer.
 */
export const shouldUseByoLivekit = (
  orgDomain?: string | null,
  _savedMode?: SessionMode,
  forceByo?: boolean,
) => (forceByo === true ? true : orgDomain ? isByoLivekitOrg(orgDomain) : false);

export const liveAvatarModeForOrg = (orgDomain?: string | null): SessionMode =>
  isByoLivekitOrg(orgDomain) ? "lite" : "full";

export const preferImportedVoices = (orgDomain?: string | null) =>
  orgDomain?.toLowerCase() === "visualnative";

/**
 * In the BYO LiveKit flow HeyGen only renders the face (`mode: "LITE"`) and our
 * own agent worker produces the speech with ElevenLabs — so it can only honor
 * voices that carry an ElevenLabs id (imported / private). Public HeyGen voice
 * ids are meaningless to the worker and it falls back to its default voice, so
 * every BYO org is restricted to imported voices, not just the explicit list.
 */
export const isImportedVoicesOnlyOrg = (orgDomain?: string | null) =>
  isByoLivekitOrg(orgDomain) || has(IMPORTED_VOICES_ONLY_ORGS, orgDomain);

export const canUseUpsellPublishMode = (orgDomain?: string | null) => has(UPSELL_PUBLISH_MODE_ORGS, orgDomain);

export const canImportLegacySessions = (orgDomain?: string | null) => has(LEGACY_IMPORT_ORGS, orgDomain);

/** Whether this org can see real session analytics next to the demo dataset. */
export const canViewLiveAnalytics = (orgDomain?: string | null) => has(LIVE_ANALYTICS_ORGS, orgDomain);

/** Whether this org sees the account's custom avatars alongside public ones. */
export const canUseUserAvatars = (orgDomain?: string | null) => has(USER_AVATAR_ORGS, orgDomain);

export type { ProviderId };

/** Providers this org can pick from in the scenario editor. */
export const getAvailableProviders = (orgDomain?: string | null): ProviderId[] => {
  if (has(TAVUS_ONLY_ORGS, orgDomain)) return ["tavus"];
  const providers: ProviderId[] = ["liveavatar"];
  if (has(TAVUS_PROVIDER_ORGS, orgDomain)) providers.push("tavus");
  if (has(DID_PROVIDER_ORGS, orgDomain)) providers.push("did");
  if (has(KALTURA_PROVIDER_ORGS, orgDomain)) providers.push("kaltura");
  return providers;
};
